<template>
  <div>
    <div class="print-show-only m-b-md">
      <h1 class="font-heavy m-b-xs">{{ teamName }} - Payments Report</h1>
      <p class="text-gray-600 text-14">Report period: {{ humanizedRange }}</p>
    </div>
    <div class="flex flex-wrap gap-x-lg gap-x-xs-xs-only gap-y-sm align-items-end print-hide">
      <div class="form-group m-b-none">
        <label class="font-normal">Viewing</label>
        <select v-model="range" id="payment-chart-range" class="form-control" name="range">
          <option value="today">Today</option>
          <option value="7d">Last 7 days</option>
          <option value="4w">Last 4 weeks</option>
          <option value="3m">Last 3 months</option>
          <option value="12m">Last 12 months</option>
          <option value="year to date">Year to date</option>
          <option value="custom">Custom date range</option>
        </select>
      </div>
      <div v-if="locations.length" class="form-group m-b-none">
        <label class="font-normal">Location</label>
        <select v-model="locationId" id="payment-chart-location" class="form-control" name="location">
          <option value="">All locations</option>
          <option v-for="location in locations" :key="location.id" :value="location.id">{{ location.name }}</option>
        </select>
      </div>
      <div v-if="range === 'custom'" class="flex flex-wrap gap-sm">
        <div class="form-group m-b-none">
          <label class="font-normal">From</label>
          <input type="date" v-model="customStartDate" class="form-control" />
        </div>
        <div class="form-group m-b-none">
          <label class="font-normal">To</label>
          <input type="date" v-model="customEndDate" class="form-control" />
        </div>
        <div v-if="error" class="form-group m-b-none">
          <p class="text-red-500">{{ errorMessage }}</p>
        </div>
      </div>
      <div class="form-group m-b-none">
        <label class="font-normal">{{ range === 'custom' ? 'Group data by' : 'Grouped' }}</label>
        <select v-model="interval" id="payment-interval" class="form-control" name="interval">
          <option v-for="i in availableIntervals" :value="i">{{ capitalize(i) }}</option>
        </select>
      </div>
      <div class="form-group m-b-none flex flex-col align-items-center" v-if="range === 'custom'">
          <button @click="applyCustomDateRange" class="btn btn-primary">Apply</button>
        </div>
      <div v-if="range !== 'custom'" class="form-group m-b-none">
        <label class="font-normal">Compared to</label>
        <select id="payment-comparison" class="form-control" name="comparison">
          <option value="previous-period">Previous period</option>
        </select>
      </div>
    </div>
    <section class="dashboard-section-divider m-t-lg p-t-lg print-avoid-break">
      <div class="flex flex-wrap align-items-start justify-space-between gap-x-m gap-y-sm m-b-m print-stack-title">
        <div>
          <div class="dashboard-section-eyebrow">
            <span class="dashboard-section-dot is-revenue"></span>
            <span>Revenue</span>
          </div>
          <p class="text-gray-500 m-t-xxs m-b-none print-hide">Revenue breakdown and recurring revenue. Click any tile to expand its chart.</p>
        </div>
        <div class="btn-group segmented-control print-hide" role="group" aria-label="Category breakdown measure">
          <button type="button" class="btn btn-default btn-compact" :class="{ active: categoryMeasure === 'net' }" :aria-pressed="categoryMeasure === 'net'" @click="categoryMeasure = 'net'">Revenue</button>
          <button type="button" class="btn btn-default btn-compact" :class="{ active: categoryMeasure === 'count' }" :aria-pressed="categoryMeasure === 'count'" @click="categoryMeasure = 'count'">Transactions</button>
        </div>
      </div>
      <div class="m-b-sm print-full-width print-scale-to-fit">
        <metrics-chart v-if="shouldShowChart('category-breakdown-net')" :key="`${keyFor('category-breakdown-net')}-${categoryMeasure}`" chart-key="category-breakdown-net" :endpoint="this.categoryBreakdownNetUrl"></metrics-chart>
      </div>
      <div v-if="showRecurring" class="metrics-strip grid grid-cols-2 md-grid-cols-1 sm-grid-cols-1 border border-solid border-gray-200 rounded-md">
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('mrr')" :key="keyFor('mrr')" chart-key="mrr" :endpoint="this.mrrUrl" :default-collapsed="true"></metrics-chart>
        </div>
        <div class="metrics-strip-cell">
          <div class="metrics-chart-collapsed cursor-pointer p-y-sm p-x-m" role="button" tabindex="0" :aria-expanded="otherRevenueExpanded" @click="otherRevenueExpanded = !otherRevenueExpanded" @keydown.enter.prevent="otherRevenueExpanded = !otherRevenueExpanded" @keydown.space.prevent="otherRevenueExpanded = !otherRevenueExpanded">
            <div class="flex align-items-center justify-space-between gap-xs m-b-xxs">
              <div class="flex-1 text-12 text-gray-700 metrics-chart-title">
                <label>Other revenue metrics</label>
              </div>
              <span class="text-12 text-gray-400 print-hide" aria-hidden="true">
                <i :class="otherRevenueExpanded ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
              </span>
            </div>
            <div class="text-12 text-gray-500">Gross volume, net volume, revenue per subscription</div>
          </div>
        </div>
      </div>
      <div v-if="showRecurring && otherRevenueExpanded" class="metrics-strip grid grid-cols-3 md-grid-cols-1 sm-grid-cols-1 border border-solid border-gray-200 rounded-md m-t-sm">
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('gross')" :key="keyFor('gross')" chart-key="gross" :endpoint="this.grossUrl" :default-collapsed="true"></metrics-chart>
        </div>
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('net')" :key="keyFor('net')" chart-key="net" :endpoint="this.netUrl" :default-collapsed="true"></metrics-chart>
        </div>
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('rev-per-sub')" :key="keyFor('rev-per-sub')" chart-key="rev-per-sub" :endpoint="this.revSubscriberUrl" :default-collapsed="true"></metrics-chart>
        </div>
      </div>
      <div v-if="!showRecurring" class="metrics-strip grid grid-cols-2 md-grid-cols-1 sm-grid-cols-1 border border-solid border-gray-200 rounded-md">
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('gross')" :key="keyFor('gross')" chart-key="gross" :endpoint="this.grossUrl" :default-collapsed="true"></metrics-chart>
        </div>
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('net')" :key="keyFor('net')" chart-key="net" :endpoint="this.netUrl" :default-collapsed="true"></metrics-chart>
        </div>
      </div>
    </section>
    <section v-if="showRecurring" class="dashboard-section-divider m-t-lg p-t-lg print-avoid-break">
      <div class="flex flex-wrap align-items-start justify-space-between gap-x-m gap-y-sm m-b-m print-stack-title">
        <div>
          <div class="dashboard-section-eyebrow">
            <span class="dashboard-section-dot is-retention"></span>
            <span>Retention</span>
          </div>
          <p class="text-gray-500 m-t-xxs m-b-none print-hide">New vs. cancelled subscriptions, by month or by membership plan, with the active base.</p>
        </div>
        <div class="btn-group segmented-control print-hide" role="group" aria-label="Retention view">
          <button type="button" class="btn btn-default btn-compact" :class="{ active: retentionView === 'month' }" :aria-pressed="retentionView === 'month'" @click="retentionView = 'month'">By month</button>
          <button type="button" class="btn btn-default btn-compact" :class="{ active: retentionView === 'plan' }" :aria-pressed="retentionView === 'plan'" @click="retentionView = 'plan'">By plan</button>
        </div>
      </div>
      <div class="m-b-sm print-full-width print-scale-to-fit">
        <metrics-chart v-if="retentionView === 'month' && shouldShowChart('subscription-flow')" :key="keyFor('subscription-flow')" chart-key="subscription-flow" :endpoint="this.subscriptionFlowUrl"></metrics-chart>
        <memberships-by-plan-table v-if="retentionView === 'plan' && shouldShowChart('memberships-by-plan')" :key="keyFor('memberships-by-plan')" :endpoint="this.membershipsByPlanUrl"></memberships-by-plan-table>
      </div>
      <div class="metrics-strip grid grid-cols-6 md-grid-cols-1 sm-grid-cols-1 border border-solid border-gray-200 rounded-md">
        <div class="metrics-strip-cell col-span-2 md-col-span-1">
          <metrics-chart v-if="shouldShowChart('new-subscriptions')" :key="keyFor('new-subscriptions')" chart-key="new-subscriptions" :endpoint="this.newSubscriptionsUrl" :default-collapsed="true"></metrics-chart>
          <a @click.prevent="openFeed('BillingSubscriptionEvent', 'event_name_eq', 'subscription.created')" class="metrics-strip-link print-hide">
            <span>View new subscriptions {{ humanizedRange }}</span> <i class="fa-solid fa-angle-right"></i>
          </a>
        </div>
        <div class="metrics-strip-cell col-span-2 md-col-span-1">
          <metrics-chart v-if="shouldShowChart('active-subscribers')" :key="keyFor('active-subscribers')" chart-key="active-subscribers" :endpoint="this.activeSubscribersUrl" :default-collapsed="true">
            <template v-slot:expanded-extras="{ metrics }">
              <div v-if="metrics.by_plan && metrics.by_plan.length" class="m-t-sm">
                <div class="text-12 text-gray-500 m-b-xxs">Active today, by plan</div>
                <table class="table table-condensed m-b-none text-12">
                  <thead>
                    <tr>
                      <th class="text-gray-600 font-semi">Plan</th>
                      <th class="text-gray-600 font-semi">Program</th>
                      <th class="text-gray-600 font-semi">Term</th>
                      <th class="text-gray-600 font-semi text-right">Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="plan in metrics.by_plan" :key="plan.plan_id">
                      <td class="text-gray-800">{{ plan.plan_name }} <span v-if="!plan.plan_active" class="text-gray-400">(inactive plan)</span></td>
                      <td class="text-gray-600">{{ plan.product_name }}</td>
                      <td class="text-gray-600">{{ plan.term_label }}</td>
                      <td class="text-gray-800 font-semi text-right">{{ plan.active_count }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </metrics-chart>
        </div>
        <div class="metrics-strip-cell col-span-2 md-col-span-1">
          <metrics-chart v-if="shouldShowChart('cancelled-subscriptions')" :key="keyFor('cancelled-subscriptions')" chart-key="cancelled-subscriptions" :endpoint="this.canceledSubscriptionsUrl" :default-collapsed="true"></metrics-chart>
          <a @click.prevent="openFeed('BillingSubscriptionEvent', 'event_name_eq', 'subscription.canceled')" class="metrics-strip-link print-hide">
            <span>View cancelled subscriptions {{ humanizedRange }}</span> <i class="fa-solid fa-angle-right"></i>
          </a>
        </div>
        <div class="metrics-strip-cell md-col-span-1" :class="showRenewed ? 'col-span-3' : 'col-span-6'">
          <metrics-chart v-if="shouldShowChart('churn-rate')" :key="keyFor('churn-rate')" chart-key="churn-rate" :endpoint="this.churnRateUrl" :default-collapsed="true"></metrics-chart>
        </div>
        <div v-if="showRenewed" class="metrics-strip-cell col-span-3 md-col-span-1">
          <metrics-chart v-if="shouldShowChart('renewed-subscriptions')" :key="keyFor('renewed-subscriptions')" chart-key="renewed-subscriptions" :endpoint="this.renewedSubscriptionsUrl" :default-collapsed="true"></metrics-chart>
          <a @click.prevent="openFeed('BillingSubscriptionEvent', 'event_name_eq', 'subscription.renewed')" class="metrics-strip-link print-hide">
            <span>View renewed subscriptions {{ humanizedRange }}</span> <i class="fa-solid fa-angle-right"></i>
          </a>
        </div>
      </div>
    </section>
    <section class="dashboard-section-divider m-t-lg p-t-lg print-avoid-break">
      <div class="flex flex-wrap align-items-start justify-space-between gap-x-m gap-y-sm m-b-m print-stack-title">
        <div>
          <div class="dashboard-section-eyebrow">
            <span class="dashboard-section-dot is-leads"></span>
            <span>Leads &amp; Conversions</span>
          </div>
          <p class="text-gray-500 m-t-xxs m-b-none print-hide">Trial pipeline — how many leads bought a trial, completed it, and converted to a paid membership.</p>
        </div>
        <a href="/prospects" class="prospects-cta print-hide flex align-items-center gap-xs text-12 font-semi text-blue-700 hover-text-blue-800">
          <span class="text-10 font-heavy bg-purple-50 text-purple-700 rounded-xxs p-xxs">Add-on</span>
          <span>Open full lead pipeline</span>
          <i class="fa-solid fa-angle-right"></i>
        </a>
      </div>
      <div class="metrics-strip grid grid-cols-3 md-grid-cols-1 sm-grid-cols-1 border border-solid border-gray-200 rounded-md">
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('trials-purchased')" :key="keyFor('trials-purchased')" chart-key="trials-purchased" :endpoint="this.trialsPurchasedUrl" :default-collapsed="true"></metrics-chart>
        </div>
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('trials-completed')" :key="keyFor('trials-completed')" chart-key="trials-completed" :endpoint="this.trialsCompletedUrl" :default-collapsed="true"></metrics-chart>
        </div>
        <div class="metrics-strip-cell">
          <metrics-chart v-if="shouldShowChart('trial-conversion-rate')" :key="keyFor('trial-conversion-rate')" chart-key="trial-conversion-rate" :endpoint="this.trialConversionRateUrl" :default-collapsed="true"></metrics-chart>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import MetricsChart from './metrics_chart.vue';
import MembershipsByPlanTable from './memberships_by_plan_table.vue';

export default {
  name: 'payment-charts',
  components: { MetricsChart, MembershipsByPlanTable },
  props: {
    showRecurring: {
      type: Boolean,
      default: false,
    },
    showRenewed: {
      type: Boolean,
      default: false,
    },
    teamName: {
      type: String,
      default: '',
    },
    locations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      range: '7d',
      interval: 'daily',
      // Empty string = all locations. Kept as a string to match the <select> value.
      locationId: '',
      currentlyExploring: null,
      // This is the model tied to the input fields
      customStartDate: this.getDefaultStartDate(),
      customEndDate: this.getDefaultEndDate(),
      // This is the effective values for the API calls, after the user clicks apply
      effectiveCustomStartDate: this.getDefaultStartDate(),
      effectiveCustomEndDate: this.getDefaultEndDate(),
      error: null,
      errorMessage: '',
      otherRevenueExpanded: false,
      // 'net' (revenue) or 'count' (transactions) for the category breakdown chart
      categoryMeasure: 'net',
      // 'month' (subscription flow chart) or 'plan' (memberships by plan table)
      retentionView: 'month',
      // Debugging settings if you only want to inspect certain charts. Helps make logging cleaner.
      debug: {
        enabled: false,
        chartsEnabled: ['category-breakdown-net', 'net'],
      },
    };
  },
  computed: {
    customStartDateForApi() {
      if (this.range === 'custom') {
        return this.effectiveCustomStartDate;
      }
      return null;
    },
    customEndDateForApi() {
      if (this.range === 'custom') {
        return this.effectiveCustomEndDate;
      }
      return null;
    },
    categoryBreakdownNetUrl() {
      return this.$apiService.categoryBreakdownNet(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId, this.categoryMeasure);
    },
    membershipsByPlanUrl() {
      return this.$apiService.membershipsByPlan(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    categoryBreakdownCountUrl() {
      return this.$apiService.categoryBreakdownCount(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    grossUrl() {
      return this.$apiService.grossVolume(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    netUrl() {
      return this.$apiService.netVolume(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    activeSubscribersUrl() {
      return this.$apiService.activeSubscribers(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    mrrUrl() {
      return this.$apiService.mrr(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    newSubscribersUrl() {
      // Deprecated
      return this.$apiService.newSubscribers(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    newSubscriptionsUrl() {
      return this.$apiService.newSubscriptions(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    canceledSubscriptionsUrl() {
      return this.$apiService.canceledSubscriptions(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    subscriptionFlowUrl() {
      return this.$apiService.subscriptionFlow(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    renewedSubscriptionsUrl() {
      return this.$apiService.renewedSubscriptions(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    revSubscriberUrl() {
      return this.$apiService.revPerSubscriber(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    chargeCountUrl() {
      return this.$apiService.chargeCount(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    chargeAvgUrl() {
      return this.$apiService.chargeAvg(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    churnRateUrl() {
      return this.$apiService.churnRate(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    trialsPurchasedUrl() {
      return this.$apiService.trialsPurchased(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    trialsCompletedUrl() {
      return this.$apiService.trialsCompleted(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    trialConversionRateUrl() {
      return this.$apiService.trialConversionRate(this.range, this.interval, this.customStartDateForApi, this.customEndDateForApi, this.locationId);
    },
    availableIntervals() {
      if (this.range === 'today') {
        return ['hourly'];
      } if (this.range === '7d') {
        return ['daily'];
      } if (this.range === '4w') {
        return ['daily', 'weekly'];
      } if (this.range === 'custom') {
        return ['daily'];
      }
      return ['monthly'];
    },
    humanizedRange() {
      if (this.range === 'today') {
        return 'today';
      } if (this.range === '7d') {
        return 'in the last 7 days';
      } if (this.range === '4w') {
        return 'in the last 4 weeks';
      } if (this.range === '3m') {
        return 'in the last 3 months';
      } if (this.range === '12m') {
        return 'in the last 12 months';
      } if (this.range === 'year to date') {
        return 'in the year to date';
      } if (this.range === 'custom') {
        return `from ${this.formatDate(this.effectiveCustomStartDate)} to ${this.formatDate(this.effectiveCustomEndDate)}`;
      }
      return '';
    },
  },
  watch: {
    range(newRange) {
      if (newRange === 'today') {
        this.interval = 'hourly';
      } else if (newRange === '7d') {
        this.interval = 'daily';
      } else if (newRange === '4w') {
        this.interval = 'weekly';
      } else if (newRange === '3m' || newRange === '12m' || newRange === 'year to date') {
        this.interval = 'monthly';
      } else if (newRange === 'custom') {
        this.interval = 'daily';
      }

      this.$notificationManager.emit('chart-range-changed', newRange, this.interval);
    },
  },
  methods: {
    shouldShowChart(chartName) {
      if (!this.debug.enabled) return true;
      return this.debug.chartsEnabled.includes(chartName);
    },
    keyFor(chartName) {
      // locationId is part of the key so changing the location filter re-mounts
      // every chart, which re-fetches from its (now location-scoped) endpoint.
      const loc = this.locationId || 'all';
      if (this.range === 'custom') {
        return `${chartName}-custom-${this.effectiveCustomStartDate}-${this.effectiveCustomEndDate}-${this.interval}-${loc}`;
      }
      return `${chartName}-${this.range}-${this.interval}-${loc}`;
    },
    capitalize(name) {
      return _.capitalize(name);
    },
    getDefaultStartDate() {
      return moment().subtract(30, 'days').format('YYYY-MM-DD');
    },
    getDefaultEndDate() {
      return moment().format('YYYY-MM-DD');
    },
    formatDate(dateString) {
      return moment(dateString).format('MMM D, YYYY');
    },
    getDaysDifference() {
      if (!this.customStartDate || !this.customEndDate) return 30;
      return moment(this.customEndDate).diff(moment(this.customStartDate), 'days');
    },
    updateCustomDateRange() {
      if (!this.customStartDate || !this.customEndDate) return;

      // Ensure dates are in YYYY-MM-DD format
      this.validateDateFormat();

      if (moment(this.customEndDate).isBefore(moment(this.customStartDate))) {
        this.customEndDate = this.customStartDate;
      }

      this.interval = 'daily';
    },
    validateDateFormat() {
      // Ensure dates are in YYYY-MM-DD format
      try {
        // Check if dates are valid
        const startDate = moment(this.customStartDate);
        const endDate = moment(this.customEndDate);

        if (!startDate.isValid()) {
          this.error = true;
          this.errorMessage = 'Invalid start date, resetting to default';
          this.customStartDate = this.getDefaultStartDate();
        }

        if (!endDate.isValid()) {
          this.error = true;
          this.errorMessage = 'Invalid end date, resetting to default';
          this.customEndDate = this.getDefaultEndDate();
        }

        // Ensure format is YYYY-MM-DD
        this.customStartDate = startDate.format('YYYY-MM-DD');
        this.customEndDate = endDate.format('YYYY-MM-DD');
      } catch (e) {
        this.error = true;
        this.errorMessage = 'Date validation error, resetting to defaults';
        // Reset to defaults if there's an error
        this.customStartDate = this.getDefaultStartDate();
        this.customEndDate = this.getDefaultEndDate();
      }
    },
    applyCustomDateRange() {
      this.updateCustomDateRange();

      // Store the values explicitly to ensure they're properly captured
      const startDate = this.customStartDate;
      const endDate = this.customEndDate;

      // Set the effective dates that will be used for API calls
      this.effectiveCustomStartDate = startDate;
      this.effectiveCustomEndDate = endDate;
    },
    openFeed(objectType, primaryPredicateKey, primaryPredicateValue) {
      let createdAtStart; let
        createdAtEnd;

      if (this.range === 'custom') {
        createdAtStart = moment(this.effectiveCustomStartDate).startOf('day');
        createdAtEnd = moment(this.effectiveCustomEndDate).endOf('day');
      } else {
        createdAtStart = this.range === 'today' ? moment().startOf('day')
          : this.range === '7d' ? moment().subtract(7, 'days').startOf('day')
            : this.range === '4w' ? moment().subtract(4, 'weeks').startOf('day')
              : this.range === '3m' ? moment().subtract(3, 'months').startOf('day')
                : this.range === '12m' ? moment().subtract(12, 'months').startOf('day')
                  : moment().startOf('year');
        createdAtEnd = moment().endOf('day');
      }

      this.currentlyExploring = {
        objectType,
        primaryPredicateKey,
        primaryPredicateValue,
        createdAtStart,
        createdAtEnd,
      };
      this.$notificationManager.emit('open-feed', this.currentlyExploring);
    },
  },
};
</script>
